<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-6">
			<div class="flex justify-between items-center flex-wrap gap-4">
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">PDPs</h1>
					<p class="text-sm text-gray-500 mt-1 m-0">Manage Product Detail Pages</p>
				</div>
				<div class="flex items-center gap-2">
					<button
						type="button"
						class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
						:disabled="isLoading"
						@click="loadPdps"
					>
						<svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
						</svg>
						Refresh
					</button>
					<button
						type="button"
						class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
						@click="showCreateModal = true"
					>
						<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
						</svg>
						Create PDP
					</button>
				</div>
			</div>
		</header>

		<!-- Main Content -->
		<main class="p-6">
			<!-- Filters Card -->
			<div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
				<div class="flex flex-wrap gap-4 items-center">
					<div class="flex-1 min-w-48">
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search PDPs..."
								class="w-full py-2 pr-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
								@input="applyFilters"
							>
						</div>
					</div>
					<div class="flex items-center gap-2">
						<span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
							{{ filteredPdps.length }} PDPs
						</span>
					</div>
				</div>
			</div>

			<!-- Loading State -->
			<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
				<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<span class="ml-3 text-gray-500 text-sm">Loading PDPs...</span>
			</div>

			<!-- Table Card -->
			<div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
				<div class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr class="bg-gray-50">
								<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-24">ID</th>
								<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
								<th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="paginatedPdps.length === 0">
								<td colspan="3" class="text-center py-12 px-4">
									<svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
										<path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
									</svg>
									<p class="text-gray-500 text-sm m-0">No PDPs found</p>
									<p class="text-gray-400 text-xs mt-1 m-0">Create a new PDP to get started</p>
								</td>
							</tr>
							<tr
								v-for="pdp in paginatedPdps"
								:key="pdp.id"
								class="border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
								@click="editPdp(pdp.id)"
							>
								<td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ pdp.id }}</td>
								<td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ pdp.name }}</td>
								<td class="py-3 px-4 text-right" @click.stop>
									<div class="flex justify-end gap-2">
										<button
											type="button"
											class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7 hover:bg-orange-1 transition-colors"
											title="Edit"
											@click="editPdp(pdp.id)"
										>
											<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
											</svg>
										</button>
										<button
											type="button"
											class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600 hover:bg-red-50 transition-colors"
											title="Delete"
											@click="confirmDelete(pdp)"
										>
											<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination -->
				<div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200">
					<div class="text-sm text-gray-500">
						Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredPdps.length) }} of {{ filteredPdps.length }} PDPs
					</div>
					<div class="flex gap-2">
						<button
							type="button"
							:disabled="currentPage === 1"
							class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
							@click="currentPage--"
						>
							Previous
						</button>
						<button
							v-for="page in visiblePages"
							:key="page"
							type="button"
							class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-10 transition-colors"
							:class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
							@click="currentPage = page"
						>
							{{ page }}
						</button>
						<button
							type="button"
							:disabled="currentPage === totalPages"
							class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
							@click="currentPage++"
						>
							Next
						</button>
					</div>
				</div>
			</div>
		</main>

		<!-- Create Modal -->
		<div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="showCreateModal = false"></div>
			<div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Create New PDP</h3>
					<button
						type="button"
						class="text-gray-400 bg-transparent border-none cursor-pointer p-1 hover:text-gray-600 transition-colors"
						@click="showCreateModal = false"
					>
						<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
				<div class="p-6">
					<div class="mb-4">
						<label class="block text-sm font-medium text-gray-700 mb-2">PDP Name</label>
						<input
							v-model="newPdpName"
							type="text"
							placeholder="Enter PDP name"
							class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							@keydown.enter="createPdp"
						>
					</div>
				</div>
				<div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
					<button
						type="button"
						class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-50 transition-colors"
						@click="showCreateModal = false"
					>
						Cancel
					</button>
					<button
						type="button"
						:disabled="!newPdpName.trim() || isSaving"
						class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-6 transition-colors"
						@click="createPdp"
					>
						<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
						</svg>
						{{ isSaving ? 'Creating...' : 'Create PDP' }}
					</button>
				</div>
			</div>
		</div>

		<!-- Toast Notification -->
		<div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
			<div
				class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
				:class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
			>
				<svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
				</svg>
				<svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				<span class="text-sm font-medium text-white">{{ toastMessage }}</span>
				<button
					type="button"
					class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer p-0 hover:opacity-100 transition-opacity"
					@click="toastMessage = ''"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import PDPService from 'services/PDPService';
import { showDeleteConfirmation } from 'utils/swal';

export default defineComponent({
	name: 'PDPsPage',
	setup() {
		const router = useRouter();
		const auth0 = useAuth0();

		const pdps = ref([]);
		const isLoading = ref(true);
		const isSaving = ref(false);

		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = 20;

		const showCreateModal = ref(false);
		const newPdpName = ref('');

		const toastMessage = ref('');
		const toastVariant = ref('success');
		let toastTimeout;

		const showToast = (message, variant = 'success') => {
			if (toastTimeout) clearTimeout(toastTimeout);
			toastMessage.value = message;
			toastVariant.value = variant;
			toastTimeout = setTimeout(() => {
				toastMessage.value = '';
			}, 3000);
		};

		const filteredPdps = computed(() => {
			if (!searchQuery.value.trim()) {
				return pdps.value;
			}
			const query = searchQuery.value.toLowerCase();
			return pdps.value.filter(pdp => 
				String(pdp.name ?? '').toLowerCase().includes(query) ||
				String(pdp.id).includes(query)
			);
		});

		const totalPages = computed(() => Math.ceil(filteredPdps.value.length / pageSize));

		const paginatedPdps = computed(() => {
			const start = (currentPage.value - 1) * pageSize;
			return filteredPdps.value.slice(start, start + pageSize);
		});

		const visiblePages = computed(() => {
			const pages = [];
			const total = totalPages.value;
			const current = currentPage.value;

			let start = Math.max(1, current - 2);
			let end = Math.min(total, current + 2);

			if (end - start < 4) {
				if (start === 1) {
					end = Math.min(total, start + 4);
				} else {
					start = Math.max(1, end - 4);
				}
			}

			for (let i = start; i <= end; i++) {
				pages.push(i);
			}
			return pages;
		});

		const applyFilters = () => {
			currentPage.value = 1;
		};

		const loadPdps = async () => {
			isLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				pdps.value = await PDPService.getAll(params);
			} catch (error) {
				showToast(error.message || 'Failed to load PDPs', 'error');
			} finally {
				isLoading.value = false;
			}
		};

		const createPdp = async () => {
			if (!newPdpName.value.trim()) return;

			isSaving.value = true;
			try {
				const newPdp = await PDPService.create({ name: newPdpName.value.trim() });
				pdps.value.unshift(newPdp);
				showToast('PDP created successfully');
				showCreateModal.value = false;
				newPdpName.value = '';
				router.push(`/pdp/${newPdp.id}`);
			} catch (error) {
				showToast(error.message || 'Failed to create PDP', 'error');
			} finally {
				isSaving.value = false;
			}
		};

		const editPdp = (id) => {
			router.push(`/pdp/${id}`);
		};

		const confirmDelete = async (pdp) => {
			const confirmed = await showDeleteConfirmation(
				'Delete PDP',
				`Are you sure you want to delete "${pdp.name}"? This action cannot be undone.`
			);

			if (confirmed) {
				try {
					await PDPService.delete(pdp.id);
					pdps.value = pdps.value.filter(p => p.id !== pdp.id);
					showToast('PDP deleted successfully');
				} catch (error) {
					showToast(error.message || 'Failed to delete PDP', 'error');
				}
			}
		};

		const loggedIn = computed(() => auth0.isAuthenticated.value);

		watch(loggedIn, async (isLoggedIn) => {
			if (isLoggedIn) {
				await loadPdps();
			}
		});

		onMounted(async () => {
			if (auth0.isAuthenticated.value) {
				await loadPdps();
			}
		});

		return {
			pdps,
			isLoading,
			isSaving,
			searchQuery,
			currentPage,
			pageSize,
			totalPages,
			showCreateModal,
			newPdpName,
			toastMessage,
			toastVariant,
			filteredPdps,
			paginatedPdps,
			visiblePages,
			applyFilters,
			loadPdps,
			createPdp,
			editPdp,
			confirmDelete,
		};
	},
});
</script>

<style>
@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
</style>
